import React from "react";
import ModeIcon from "../../components/ModeIcon";

export type LeagueMode =
  | "TARGET"
  | "GAMEWEEK_CHALLENGE"
  | "FINAL_POSITIONS"
  | "SCORE_PREDICTOR"
  | "SYNDICATE";

export type EngagementBucket = "FIRE_AND_FORGET" | "WEEKLY";

/** Creatable modes — drives the landing page cards, the create-league picker,
 * and (via the CI drift tests in __tests__/lib) which translation keys must exist. */
export const ALL_LEAGUE_MODES: LeagueMode[] = [
  "TARGET",
  "GAMEWEEK_CHALLENGE",
  "FINAL_POSITIONS",
  "SCORE_PREDICTOR",
  "SYNDICATE",
];

/** Modes that need a config step at creation. */
export function modeHasConfig(mode: LeagueMode): boolean {
  return mode === "TARGET" || mode === "GAMEWEEK_CHALLENGE";
}

/** Modes that offer a start-gameweek picker. */
export function modeHasStartGw(mode: LeagueMode): boolean {
  return ALL_LEAGUE_MODES.includes(mode);
}

// ── SVG Icons ────────────────────────────────────────────────────────────────
// Geometry lives in components/ModeIcon.tsx (the single source, shared with the
// 13px ModeIconSmall badge) — these are just mode-bound thin wrappers so existing
// `MODE_CONTENT[mode].icon` callers don't need to change.

export const IconTarget = () => React.createElement(ModeIcon, { mode: "TARGET" });
export const IconGameweek = () => React.createElement(ModeIcon, { mode: "GAMEWEEK_CHALLENGE" });
export const IconPositions = () => React.createElement(ModeIcon, { mode: "FINAL_POSITIONS" });
export const IconPredict = () => React.createElement(ModeIcon, { mode: "SCORE_PREDICTOR" });
export const IconSyndicate = () => React.createElement(ModeIcon, { mode: "SYNDICATE" });

// ── Effort tier ───────────────────────────────────────────────────────────────

/** Maps numeric effort (1–5) to a word tier for display in combined chip. */
export function effortTier(effort: number): "low" | "medium" | "high" {
  if (effort <= 2) return "low";
  if (effort === 3) return "medium";
  return "high";
}

// ── Mode metadata ─────────────────────────────────────────────────────────────

export interface ModeContent {
  /** i18n key root under createLeague.mode.<MODE> */
  keyRoot: string;
  icon: () => React.ReactElement;
  engagement: EngagementBucket;
  /** Ongoing effort required — 1 (fire & forget) to 5 (weekly commitment) */
  effort: number;
  hasConfig: boolean;
  hasStartGw: boolean;
}

/** Card/badge accent colour per mode — the `--color-mode-*` tokens in index.css. */
export const MODE_COLOR_VAR: Record<LeagueMode, string> = {
  TARGET: "var(--color-mode-target)",
  GAMEWEEK_CHALLENGE: "var(--color-mode-gameweek-challenge)",
  FINAL_POSITIONS: "var(--color-mode-final-positions)",
  SCORE_PREDICTOR: "var(--color-mode-score-predictor)",
  SYNDICATE: "var(--color-mode-syndicate)",
};

export const MODE_CONTENT: Record<LeagueMode, ModeContent> = {
  TARGET: {
    keyRoot: "createLeague.mode.TARGET",
    icon: IconTarget,
    engagement: "FIRE_AND_FORGET",
    effort: 2,
    hasConfig: true,
    hasStartGw: true,
  },
  GAMEWEEK_CHALLENGE: {
    keyRoot: "createLeague.mode.GAMEWEEK_CHALLENGE",
    icon: IconGameweek,
    engagement: "WEEKLY",
    effort: 4,
    hasConfig: true,
    hasStartGw: true,
  },
  FINAL_POSITIONS: {
    keyRoot: "createLeague.mode.FINAL_POSITIONS",
    icon: IconPositions,
    engagement: "FIRE_AND_FORGET",
    effort: 2,
    hasConfig: false,
    hasStartGw: false,
  },
  SCORE_PREDICTOR: {
    keyRoot: "createLeague.mode.SCORE_PREDICTOR",
    icon: IconPredict,
    engagement: "WEEKLY",
    effort: 4,
    hasConfig: false,
    hasStartGw: false,
  },
  SYNDICATE: {
    keyRoot: "createLeague.mode.SYNDICATE",
    icon: IconSyndicate,
    engagement: "FIRE_AND_FORGET",
    effort: 2,
    hasConfig: false,
    hasStartGw: true,
  },
};
